import { DateTimeFormatter, Duration, Instant, LocalDateTime } from "@js-joda/core";
import { match } from "ts-pattern";
import {
  CommCenterMessageActionId,
  CommCenterMessageId,
  CommCenterTicketId,
} from "../../../messages";
import {
  CaregiverCommCenterMessage,
  CommCenterCaregiverTicketList,
  CommCenterGetTicketResponse,
  CommCenterMessage,
  CommCenterMessageActionEntity,
  CommCenterMessagePayloadActionList,
  CommCenterTicket,
} from "../../../messages/CommunicationCenter";
import { AppCaregiver } from "../../../system/AppState/AppCaregiverState";
import { globalize } from "../../../translate";
import { assertNotNull } from "../../../utils/assertUndefinedable";

export type ChatMessageTicksType = "sent" | "delivered" | "read";

export interface TypingPerson {
  name: string;
  avatarUrl: string | null;
}

export interface CommCenterListActionItem {
  entity: CommCenterMessageActionEntity;
  id: number;
  text: string;
  icon?: string | undefined;
  code?: string;
}

export function parseChatMessageTicks(message: CommCenterMessage): ChatMessageTicksType {
  if (message.readAt !== null) {
    return "read";
  }

  if (CommCenterMessageId.unwrap(message.id) > 0) {
    return "delivered";
  }

  return "sent";
}

export function getLastMessageListPayload(
  ticket: CommCenterTicket
): { payload: CommCenterMessagePayloadActionList; actionId: CommCenterMessageActionId } | null {
  if (ticket.messages.length > 0) {
    for (const payload of ticket.messages[ticket.messages.length - 1].payload) {
      if (payload.type === "ACTION" && payload.payload.payloadType === "LIST") {
        return {
          payload: payload.payload,
          actionId: assertNotNull(payload.id),
        };
      }
    }
  }

  return null;
}

export function getLastChatMessage(ticket: CommCenterTicket) {
  for (let i = ticket.messages.length - 1; i >= 0; i--) {
    const message = ticket.messages[i];
    let text: string | null = "";

    for (const payload of message.payload.reverse()) {
      text = match(payload)
        .with({ type: "TEXT" }, (data) => data.message)
        .with({ type: "IMAGE" }, () => null)
        .with({ type: "DOCUMENT" }, () => null)
        .with({ type: "VIDEO" }, () => null)
        .with({ type: "ACTION", payload: { payloadType: "TEXT" } }, (data) => data.payload.text)
        .with({ type: "ACTION", payload: { payloadType: "IMAGE" } }, () => null)
        .with({ type: "ACTION", payload: { payloadType: "DOCUMENT" } }, () => null)
        .with({ type: "ACTION", payload: { payloadType: "VIDEO" } }, () => null)
        .with({ type: "ACTION", payload: { payloadType: "LIST" } }, () => null)
        .with({ type: "ONBOARDING_MESSAGE" }, () => null)
        .exhaustive();

      if (text !== null) {
        return text;
      }
    }
  }

  return null;
}

export function sortTicketsByLastMessage(a: CommCenterTicket, b: CommCenterTicket) {
  const aLastMessage = a.messages.at(-1);
  const bLastMessage = b.messages.at(-1);

  if (aLastMessage === undefined && bLastMessage === undefined) {
    return 0;
  }

  if (aLastMessage === undefined) {
    return 1;
  }

  if (bLastMessage === undefined) {
    return -1;
  }

  return bLastMessage.createdAt.compareTo(aLastMessage.createdAt);
}

export function buildSendMessagePayload(params: {
  text: string;
  images: string[];
  documents: string[];
  videos: string[];
}) {
  const payload: CaregiverCommCenterMessage = [];

  if (params.text.length > 0) {
    payload.push({
      type: "TEXT",
      message: params.text,
    });
  }

  for (const image of params.images) {
    payload.push({
      type: "IMAGE",
      url: image,
    });
  }

  for (const document of params.documents) {
    payload.push({
      type: "DOCUMENT",
      url: document,
    });
  }

  for (const video of params.videos) {
    payload.push({
      type: "VIDEO",
      url: video,
    });
  }

  return payload;
}

export function getTotalUnreadMessages(tickets: CommCenterTicket[]) {
  return tickets.reduce((acc, ticket) => {
    return acc + ticket.messages.filter(isChatMessageUnread).length;
  }, 0);
}

export function isChatMessageUnread(message: CommCenterMessage) {
  return message.readAt === null && message.createdBy.type !== "Caregiver";
}

export function getLastMessageDateTimeString(lastMessageDateTime: LocalDateTime) {
  const isMessageFromToday =
    Duration.between(lastMessageDateTime, LocalDateTime.now()).toDays() < 1 &&
    LocalDateTime.now().dayOfWeek === lastMessageDateTime.dayOfWeek;

  if (isMessageFromToday) {
    return lastMessageDateTime.toLocalTime().format(DateTimeFormatter.ofPattern("HH:mm"));
  }

  return globalize().formatDayOfWeek(lastMessageDateTime.toLocalDate().dayOfWeek(), true);
}

export function getTicketListWithAllMessagesRead(old: CommCenterCaregiverTicketList | undefined) {
  if (old === undefined) {
    return old;
  }

  const now = Instant.now();

  return {
    ...old,
    tickets: old.tickets.map((ticket) => ({
      ...ticket,
      messages: ticket.messages.map((message) => ({
        ...message,
        readAt:
          message.readAt === null && message.createdBy.type !== "Caregiver" ? now : message.readAt,
      })),
    })),
  };
}

export function getChatRoomOptimisticUpdateData(params: {
  old: CommCenterGetTicketResponse;
  caregiver: AppCaregiver;
  ticketId: CommCenterTicketId;
  message: CaregiverCommCenterMessage;
}): CommCenterGetTicketResponse {
  const newMessages: CommCenterMessage[] = [
    ...params.old.ticket.messages,
    {
      id: CommCenterMessageId.wrap(Instant.now().nano() * -1),
      createdBy: {
        type: "Caregiver",
        id: params.caregiver.id,
        name: [params.caregiver.firstName, params.caregiver.lastName].join(" "),
      },
      createdAt: Instant.now(),
      labelId: null,
      messageActionId: null,
      payload: params.message.map((message) =>
        match(message)
          .with({ type: "TEXT" }, (m) => ({
            type: "TEXT" as const,
            message: m.message,
          }))
          .with({ type: "IMAGE" }, (m) => ({
            type: "IMAGE" as const,
            url: m.url,
          }))
          .with({ type: "DOCUMENT" }, (m) => ({
            type: "DOCUMENT" as const,
            url: m.url,
          }))
          .with({ type: "VIDEO" }, (m) => ({
            type: "VIDEO" as const,
            url: m.url,
          }))
          .with({ type: "ACTION" }, (m) => ({
            type: "TEXT" as const,
            message: m.entitiyText,
          }))
          .exhaustive()
      ),
      readAt: null,
      ticketId: params.ticketId,
    },
  ];

  return {
    ...params.old,
    ticket: {
      ...params.old.ticket,
      messages: newMessages,
    },
  };
}
